/**
 * Every balance number that is not about one specific item — **D69, T136**.
 *
 * 🎯 **haugur, 2026-08-12, and it outranked everything else in five rounds of
 * gameplay notes:** *"remember all these numbers, cooking time, grow time etc
 * etc, need to be tweakable so they can be easily changed by you for when we
 * start testing gameplay."*
 *
 * 🔴 **That is an architecture requirement wearing the clothes of a
 * preference.** There will be fifty or more of these. Scattered through twelve
 * files as `const COOK_SECONDS = 15`, gathering them up afterwards is a real
 * job; started as one file it costs nothing. It is also **D11** — content in
 * data files, not code — applied to balance, and **D2**: a non-coder has to be
 * able to change them.
 *
 * ## 🔴 Every duration carries its clock in its NAME
 *
 * The world runs at **72× real time**, so *"fifteen seconds"* is ambiguous by a
 * factor of 72 — fifteen *game* seconds is a fifth of a real second. So the
 * fields are `cookGameHours`, `woodBurnGameHours`, `hungerZeroGameDays`.
 * ⚠️ **A field named `cookTime` must not be added.** The name is the guard.
 *
 * ⚠️ **Changing a number here needs a restart, not a redeploy of thinking.**
 * Live reloading is an optimisation for later and deliberately not built.
 */

import tuningData from '../data/tuning.json' with { type: 'json' };
import { REAL_MS_PER_GAME_DAY } from './clock.ts';

export interface Tuning {
  readonly health: {
    readonly maxPercent: number;
    /** ⏱️ **GAME** days from full to empty. 3 game days is 1 real hour. */
    readonly hungerZeroGameDays: number;
    /** ⏱️ How much health comes back per **GAME** hour while there is food to pay for it. */
    readonly regenHealthPercentPerGameHour: number;
    /**
     * What one point of health costs in food.
     *
     * 🎯 **haugur, 2026-08-20:** *"loosing health (like from cold) forces you to
     * burn food to rebuild health."* This is the price.
     */
    readonly foodPercentPerHealthPercent: number;
    /** ⏱️ How much health hunger takes per **GAME** hour once food is gone. */
    readonly starveHealthPercentPerGameHour: number;
    /**
     * 🔴 **How far hunger can take you down, and no further.**
     *
     * **1 % — so nothing in the game can kill you yet**, which is haugur's own
     * call of 2026-08-20: death waits for the hearth. See the notes in
     * `tuning.json`, which carry the reasoning.
     */
    readonly starvationFloorPercent: number;
  };
  /**
   * 🥶 What the weather costs you — **D64, D85, T131**.
   *
   * ⚠️ **Every one of these is a PLAYTEST number.** ADR-0016 leaves all of them
   * deliberately undecided, and §9.3 of the survival research is blunt that this
   * is the one system where guessing them is how it turns cruel. **Ship soft,
   * raise it after somebody has felt it** — the way the hunger rate was.
   */
  readonly exposure: {
    /**
     * 🌡️ Below this the cold bites, in °C.
     *
     * 🎯 **haugur's call, 2026-08-21: frost only.** Iceland averages about 5 °C,
     * so a 5 °C threshold would mean cold outdoors for **half the game year** —
     * with no clothing, no fire and no real shelter yet, and therefore nothing a
     * player could DO about it. 👉 **Raise it toward 5 once wool and a hearth
     * exist.**
     */
    readonly coldBelowCelsius: number;
    /**
     * 💨 At or above this the wind bites, in m/s.
     *
     * 10 sits at the top of **stinningskaldi**, just under **allhvass vindur** —
     * a noticeably windy day rather than every day.
     */
    readonly windAboveMetresPerSecond: number;
    /** How much cold adds to the food drain. 0.7 → 1.7× on its own. */
    readonly coldSurcharge: number;
    /** How much wind adds. **D64 §3: wind is what actually kills people here.** */
    readonly windSurcharge: number;
    /**
     * 🔴 **How much being wet adds — and it is `0` on purpose.**
     *
     * 🎯 **haugur, 2026-08-21:** *"we have not designed rain or snow yet, yet
     * those have a say in it when you get cold."* ⚠️ **No rain is drawn falling
     * and no snow lies on the ground**, so a player made cold by wet could not
     * see why — the exact fault §6 bans a temperature field to avoid. Built,
     * typed and tested; it simply costs nothing yet.
     */
    readonly wetSurcharge: number;
    /**
     * 🏠 How much of the cold survives being indoors, 0…1.
     *
     * **D64 §3 gives an enclosed house "most of the cold removed"** — not all.
     * A turf house with no fire in it is still cold, and the full stop belongs
     * to the fire alone.
     */
    readonly indoorsColdShare: number;
    /**
     * 👕 The MOST that clothing can ever take off, 0…below 1.
     *
     * 🎯 **D64 §5: clothing reduces, it does not cancel.** The ceiling is the
     * mechanism — wool buys you a great deal and never makes you immune.
     * ⚠️ Nothing can wear anything yet, so this is reachable only in tests.
     */
    readonly clothingReductionCeiling: number;
  };
  /**
   * 🫐 Picking berries off the heath (T129).
   *
   * ⚠️ **`pickedFromBlock` is a block KEY**, so which block bears berries is
   * data rather than a literal in the server — the day `kjarr` or a berry slab
   * bears them too, this is where it is said.
   */
  readonly berries: {
    readonly pickedFromBlock: string;
    /** ⏱️ **GAME** hours before the same patch gives another. 6 is 5 real minutes. */
    readonly regrowGameHours: number;
  };
  readonly inventory: {
    readonly defaultStackSize: number;
  };
  readonly cooking: {
    /**
     * ⏱️ **GAME** hours to turn one raw thing into its cooked one — **T222**.
     *
     * 🎯 **haugur chose it as SIXTEEN FISH TO A BIRCH LOG**, 2026-09-22. A log
     * burns 6 game hours, so this is **0.375 — 18¾ real seconds.**
     *
     * 🔴 **`stoveCookRealSeconds` and `campfireCookMultiplier` LEFT THIS TYPE on
     * 2026-09-22, and it is not tidying.** They said **15** and **3** — 45 real
     * seconds on a campfire, 🎯 his own numbers of 2026-08-12 — **and nothing
     * ever read them.** They were chosen while a wood burned for **1** game
     * hour; **D86** raised it to **6** and nothing re-read the line, so *"45
     * real seconds"* had quietly become about **6½ fish to a log**.
     * ⚠️ **That is the `charcoalBurnGameHours` fault directly above, in the same
     * file, a second time**: a value that did not change stopped meaning what it
     * meant, because the thing it was measured against moved. ✅ **He was shown
     * both and chose knowingly.**
     */
    readonly cookGameHours: number;
  };
  readonly fuel: {
    /**
     * 🔴 **`charcoalBurnGameHours` LEFT THIS TYPE on 2026-08-25**, and it
     * is not tidying.
     *
     * It said **5**, and the note beside it said *charcoal burns 5× longer than
     * wood* — both true together **only while a wood burned for 1 game hour**.
     * **D86** raised wood to **6** and left this at 5, so **wood outlasted
     * charcoal and the whole fuel ladder inverted**, with every sentence in the
     * file still reading as though it had not.
     *
     * ✅ It now lives on the **`vidarkol`** item at **30 game hours**, beside the
     * wood it is measured against. 👉 **Two numbers that only mean
     * anything together must not live in two files.**
     */
    readonly charcoalPerWoodBurned: number;
  };
  readonly trees: {
    readonly saplingToSmallGameYears: number;
    readonly smallToFullGameYears: number;
  };
  readonly animals: {
    readonly fodderPerAnimalPerGameMonth: number;
  };
  readonly breaking: {
    /**
     * ⏱️ **REAL** seconds of holding that one `click` is worth.
     *
     * 🎯 **haugur, 2026-08-17:** hold the button rather than press it that many
     * times. **His numbers did not change meaning** — a click is still the unit
     * of difficulty, it is now how *long* rather than how many presses.
     */
    readonly perClickRealSeconds: number;
  };
  /**
   * How much each tool divides a block's bare-handed click count by.
   *
   * 🎯 **haugur, 2026-08-13:** *"just make sure we can change this table later
   * on."* 👉 **These four numbers are that table.** Change one and every block
   * in `block-behaviour.json` moves with it.
   *
   * ⚠️ **Four, not three, since 2026-09-01 — there is a stone rung now (D103)**,
   * and iron moved from ×3.33 to ×10 in the same change. Both are haugur's own
   * numbers; `tuning.json` carries the sentence he gave them in.
   */
  readonly tools: {
    readonly speedBareHanded: number;
    readonly speedWoodTool: number;
    readonly speedStoneTool: number;
    readonly speedIronTool: number;
    /**
     * 🔴 **A TESTING AID THAT IS PRESENT ONLY WHILE SOMEBODY IS TESTING.**
     *
     * 🎯 **haugur, 2026-09-02:** *"on staging make stone tools x1 hit to break
     * everything and wood tools x2 hits, make a note for use to change this back
     * later, this helps me test faster."*
     *
     * ✅ **Optional on purpose, and a separate object on purpose.** The four
     * numbers above are **D103** — haugur's own ratio, quoted in the Project
     * Brief. Overwriting them would make the brief lie and would retune the game
     * for ever if anybody forgot. Kept apart: the design stays readable, a test
     * asserts it is untouched, and **restoring the game is deleting one key.**
     */
    readonly testOverride?: {
      readonly speedWoodTool: number;
      readonly speedStoneTool: number;
      readonly speedIronTool: number;
    };
  };
}

/* The `$`-prefixed keys in the JSON are documentation for whoever opens the
   file — they are deliberately not on the interface, so nothing can read one by
   accident and no note becomes load-bearing. */
export const TUNING: Tuning = tuningData;

/**
 * Game hours as real milliseconds.
 *
 * 🔴 **The one place that converts between the two clocks**, so a duration
 * written on the game clock never has to be turned into real time by hand — and
 * the 72× lives in `clock.ts` alone rather than being restated here.
 */
export function gameHoursToRealMs(gameHours: number): number {
  return (gameHours / 24) * REAL_MS_PER_GAME_DAY;
}

/** Game days as real milliseconds. */
export function gameDaysToRealMs(gameDays: number): number {
  return gameDays * REAL_MS_PER_GAME_DAY;
}
